import Ember from 'ember';
import { action } from '@ember/object';
import { task } from 'ember-concurrency';
import { timeout } from 'ember-concurrency';
import { inject as service } from '@ember/service';

import config from 'cio/config/environment';
import PersonAttributesEditController from 'cio/pods/person/attributes/edit/controller';
import RouterService from '@ember/routing/router-service';
import CustomerModel from 'cio/models/customer';

export default class NewController extends PersonAttributesEditController {
  @service router!: RouterService;

  declare model: CustomerModel;

  interval = config.APP.peopleDelay;
  isNew = true;

  // Since customer creation is async, we don't get immediate feedback
  // on whether and when the change actually gets persisted.
  // We issue a GET request after a timeout to see if the customer
  // has been created yet.
  isCustomerCreated = task(async () => {
    this.alerts.sticky('person.create', 'Creating the person…');

    if (!Ember.testing) {
      await timeout(this.interval);
    }

    try {
      let apiCustomer;

      if (
        this.environment.current.isEmailIdentified ||
        (this.environment.current.isMultiIdentifier && !this.model.attrs.id)
      ) {
        let apiCustomers = await this.store.query('customer', { email: this.model.attrs.email });

        // The back end doesn't return an error here, but instead an empty array. In this case, we
        // throw our own error to trigger the 404 case in the catch below.
        if (!apiCustomers.length) {
          throw { errors: [{ status: '404' }] };
        }

        apiCustomer = apiCustomers.slice()[0];
      } else {
        apiCustomer = await this.store.findRecord('customer', this.model.attrs.id as string);
      }

      this.alerts.remove('person.create');
      this.attributesProxy?.reset();
      this.router.transitionTo('person', apiCustomer as CustomerModel);

      return true;
    } catch (e) {
      this.alerts.remove('person.create');

      if (e.errors[0].status === '404') {
        this.alerts.warning('person.create', 'Your customer will be added soon.');
        this.attributesProxy?.reset();
        this.router.transitionTo('people');

        return false;
      }

      throw e;
    }
  });

  saveTask = task(async (attrs: any) => {
    let customer = this.model;

    if (attrs !== null) {
      customer.set('attrs', attrs);
    }

    try {
      await customer.saveAndQueue({});
    } catch (err) {
      return this.processServerError(err);
    }

    this.model.updateSentAttributes(attrs);

    await this.isCustomerCreated.perform();
  });

  @action
  reset() {
    this.model.rollbackAttributes();
    this.router.transitionTo('people');
  }
}
